import {
  Injectable,
} from 'ng-metadata/core';

import { ApiService } from 'services/api.service';

@Injectable()
export class CheckoutService extends ApiService {
  public GW = (<any>window).GW;
  public laroute = (<any>window).laroute;
  public remarketing = this.GW.remarketing;

  getNewOrderPayload(cart: any, order: any, merchant: any, nonce?) {
    const payload: any = {};

    // Add flag if user was logged in
    if (this.GW.user) {
      payload.loggedIn = true;
    }

    // Add merchant
    payload.merchant = merchant.merchant;

    // Add source
    if (typeof this.GW.location === 'undefined') throw 'GW location data missing';
    payload.source = this.GW.location.host;

    // Add itinerary
    payload.itinerary = {
      destinations: Object.values(cart.itinerary.destinations),
      travelers: cart.itinerary.travelers,
    };

    // Add coupon code
    if (cart.coupon) {
      payload.coupon_code = cart.coupon.coupon;
    }

    // Add remarketing information
    payload.remarketing = this.remarketing;

    payload.travelers = [];
    for (const travelerId in cart.travelers) {
      const traveler = cart.travelers[travelerId];
      payload.travelers.push({
        info: {
          first_name: traveler ? traveler.firstName : null,
          last_name: traveler ? traveler.lastName : null,
          friendly_name: traveler ? traveler.name : 'Traveler',
          citizenship: traveler ? traveler.citizenship : null,
          residency: traveler ? traveler.residency : null,
          date_of_birth: traveler ? traveler.dob : null,
        },
        products: cart.order.filter((product: any) => product.travelerId === traveler.id)
          .map((orderItem) => {
            const option = cart.options[orderItem.optionsId];
            const productData = cart.products[orderItem.productId];
            const item = {
              uuid: orderItem.productId,
              option_uuid: orderItem.optionsId,
              option_label: option.label,
              product_label: productData.label,
              service_type: option.internal, // what is this for?
              cost: option.processingFee,
              addons: this.etomsService.enabled()
                ? orderItem.addons
                : orderItem.addons && orderItem.addons.map(addon => addon.uuid),
            };

            if (orderItem.requireShipping) {
              item['outbound_speed'] = orderItem.outbound.value
            }

            return item;
          }),
      });
    }

    // Add user account details
    payload.user_profile = {
      applicant_id: order.account.applicantId,
      primary_applicant_id: order.account.primaryApplicantId,
      saleslead_id: order.account.salesleadId,
      session_id: order.account.sessionId,
      first_name: order.account.firstName,
      last_name: order.account.lastName,
      email: order.account.email,
      phones: order.account.phones,
      country: order.account.country,
      company_name: order.account.company || null,
    };

    // Prepare shipping and billing addresses, set to be equal manually
    const shippingAddress = this.addressMapping('shipping', order.shippingAddress);
    const billingAddress = this.addressMapping('billing', order.shippingAddress);

    payload.user_profile.addresses = [
      billingAddress,
      shippingAddress,
    ];

    // Add session's saved marketing campaign parameters to payload
    if (this.GW.gw_tracker) {
      payload.gw_tracker = this.GW.gw_tracker;
    }

    // Add Stripe token / Authorize nonce
    if (nonce) {
      payload.payment_token = nonce.dataValue;
      payload.payment_descriptor = nonce.dataDescriptor;
    }

    // Add the credit card to the payload, required by ETOMS' API
    if (merchant === 'etoms' && nonce) {
      payload.creditCard = nonce;
    }

    return payload;
  }

  paymentErrorHandler(error) {
    const content = {
      icon: '/img/errors/error.svg',
      title: 'An error has occurred',
      text: 'Please call our customer service department for help at 800-963-7000.',
    };

    if (!error) {
      return content;
    }

    // Handle payment errors
    switch (error.status_code) {
      case 27:
      case 666:
        content.icon = '/img/errors/credit-card-declined.svg';
        content.title = 'Credit Card Declined';
        content.text = `Your financial institution denied the charge, please check with your
          credit card company to see if they will authorize the transaction. If you continue
          to experience problems, please call <a href="tel:${this.GW.contact.phone_link}">
          ${this.GW.contact.phone}</a> and we’ll help you complete your order.`;
        break;

      case 27001:
        content.icon = '/img/errors/credit-card-declined.svg';
        content.title = 'Credit Card Protection';
        content.text = `Please wait at least 2 minutes before trying to use the same credit card
          again or you can provide a different credit card number. If you continue
          to experience problems, please call <a href="tel:${this.GW.contact.phone_link}">
          ${this.GW.contact.phone}</a> and we’ll help you complete your order.`;
        break;

      case 6:
      case 42:
      case 113:
      case 114:
      case 115:
      case 116:
      case 4269:
        content.icon = '/img/errors/credit-card-info-error.svg';
        content.title = 'Credit Card Information Error';
        content.text = `Please make sure you entered your credit card information correctly.
          If you continue to experience problems, please call
          <a href="tel:${this.GW.contact.phone_link}">${this.GW.contact.phone}</a> and we’ll help
          you complete your order.`;
        break;
      default:
        if (error.message !== undefined) {
          content.title = error.message;
          content.text = `Please make sure you entered your credit card information correctly.
          If you continue to experience problems, please call
          <a href="tel:${this.GW.contact.phone_link}">${this.GW.contact.phone}</a> and we’ll help
          you complete your order.`;
        }
        break;
    }

    return content;
  }

  determineTermsLink() {
    const href = window.location.href;
    let termsUrl;

    if (href.indexOf('travelvisa') > -1 || href.indexOf('rushmytravelvisa') > -1) {
      termsUrl = this.laroute.route('visas.terms');
    } else if (href.indexOf('passport') > -1) {
      termsUrl = this.laroute.route('passports.terms');
    }

    return termsUrl;
  }

  determinePrivacyLink() {
    const href = window.location.href;
    let termsUrl;

    if (href.indexOf('travelvisa') > -1 || href.indexOf('rushmytravelvisa') > -1) {
      termsUrl = this.laroute.route('visas.privacy');
    } else if (href.indexOf('passport') > -1) {
      termsUrl = this.laroute.route('passports.privacy');
    }

    return termsUrl;
  }

  addressMapping(type, address) {
    return {
      type,
      address_1: address.address,
      address_2: address.address2,
      city: address.locality,
      state: address.region,
      country: address.country,
      postal_code: address.postalCode,
    };
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/checkout/checkout.service.ts